<script lang="ts" setup>
import { computed, ref, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { EmptyState, PageHero } from '~/components';
import {
  AppSkeletonLoader,
  AppTextInput,
  AppTypography,
} from '~/components/base';
import { CandidateCard, CandidatePartyModal } from '~/components/candidates';
import { LocationPickerModal } from '~/components/layout';
import { useCandidates } from '~/composables/useCandidates';
import { useInfiniteScroll } from '~/composables/useInfiniteScroll';
import { useQuerySettling } from '~/composables/useQuerySettling';
import { useLocationStore } from '~/stores/location';
import type { Chamber } from '~/types/Candidates';

definePageMeta({ layout: 'default' });
useHead({ title: 'Candidates' });
useSeoMeta({
  description:
    'Browse local and regional candidates for the Victorian State Election, with their parties, campaigns and lobbyist links in one place.',
  ogTitle: 'Candidates | VoteWise',
  ogDescription:
    'Browse local and regional candidates for the Victorian State Election, with their parties, campaigns and lobbyist links in one place.',
});

const ALL_PARTIES = 'All Parties';
const INDEPENDENT = 'Independent';
// Not a party — a sitting-member filter that shares the party dropdown's
// single-select slot, so picking it clears any party narrowing (and vice
// versa). Mapped to the composable's `incumbentOnly` boolean below.
const INCUMBENT = 'Incumbent';

// Mobile shows the short "District"/"Region" tab label; desktop shows the
// full "District Candidates"/"Region Candidates" phrasing.
const TABS: { value: Chamber; label: string; desktopLabel: string }[] = [
  { value: 'lower', label: 'District', desktopLabel: 'District Candidates' },
  { value: 'upper', label: 'Region', desktopLabel: 'Region Candidates' },
];

const route = useRoute();
const router = useRouter();

const chamber = ref<Chamber>(
  route.query.chamber === 'upper' ? 'upper' : 'lower',
);
// VTW-504: the layout renders `<NuxtPage :page-key="route.fullPath">`, so
// changing the `chamber` query param on tab switch (below) fully remounts
// this page — any state that only lives in a local ref (search text, the
// party filter) would otherwise reset to its initial value along with it.
// Seeding from — and writing back to — the route query survives that
// remount, the same pattern search.vue already uses for its own `q`.
const query = ref(
  typeof route.query.search === 'string' ? route.query.search : '',
);
const party = ref<string>(
  typeof route.query.party === 'string' ? route.query.party : ALL_PARTIES,
);
const location = useLocationStore();
const locationModalVisible = ref(false);

function setChamber(value: Chamber) {
  if (chamber.value === value) return;
  chamber.value = value;
  // Any future router.replace/push on this route must merge `search` and
  // `party` the same way, or the remount above will silently drop them.
  router.replace({
    query: {
      ...route.query,
      chamber: value,
      search: query.value || undefined,
      party: party.value !== ALL_PARTIES ? party.value : undefined,
    },
  });
}

const { candidates, partyOptions, hasMore, loadMore, pending, error } =
  useCandidates(() => ({
    chamber: chamber.value,
    query: query.value,
    party: party.value === INCUMBENT ? null : party.value,
    incumbentOnly: party.value === INCUMBENT,
    suburbId: location.location?.suburbId,
  }));

const settling = useQuerySettling(query);
// Nothing to show yet: either a request is in flight or one is about to be, so
// the page must not render a "nothing found" verdict about it.
const isLoading = computed(
  () => (pending.value || settling.value) && !candidates.value.length,
);

// An empty result means two different things: nothing matched what the user
// asked for (recoverable — tell them how to broaden it), or there is simply
// nothing to show yet. "Check the spelling" is wrong advice in the latter case.
// Location is deliberately excluded — a suburb isn't something you misspell.
const isNarrowed = computed(
  () => query.value.trim().length > 0 || party.value !== ALL_PARTIES,
);

const partyDropdownOptions = computed(() => [
  { label: 'All', value: ALL_PARTIES },
  ...partyOptions.value.map((name) => ({ label: name, value: name })),
  { label: INDEPENDENT, value: INDEPENDENT },
  { label: INCUMBENT, value: INCUMBENT },
]);

const partyModalVisible = ref(false);

const partyLabelDesktop = computed(() =>
  party.value === ALL_PARTIES ? 'All Parties' : partyLabel.value,
);

const partyLabel = computed(
  () =>
    partyDropdownOptions.value.find((option) => option.value === party.value)
      ?.label ?? 'All',
);

// Infinite scroll: auto-load as the sentinel nears the viewport; the
// Show More button remains as fallback.
const sentinel = useTemplateRef<HTMLElement>('sentinel');
useInfiniteScroll(sentinel, loadMore);
</script>

<template>
  <div class="candidates-page">
    <PageHero constrained>
      <div class="candidates-page__panel">
        <div
          class="candidates-page__toolbar flex flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:gap-4"
        >
          <nav
            class="candidates-page__tabs order-3 flex w-full items-stretch lg:order-1 lg:mr-auto lg:w-auto"
          >
            <button
              v-for="tab in TABS"
              :key="tab.value"
              type="button"
              :data-testid="`chamber-tab-${tab.value}`"
              class="candidates-page__tab flex-1 lg:flex-none"
              :class="{
                'candidates-page__tab--active': chamber === tab.value,
              }"
              @click="setChamber(tab.value)"
            >
              <span class="lg:hidden">{{ tab.label }}</span>
              <span class="hidden lg:inline">{{ tab.desktopLabel }}</span>
            </button>
          </nav>

          <div
            data-testid="candidates-search"
            class="candidates-page__search order-1 flex items-center gap-2 lg:order-3"
          >
            <Icon
              name="solar:magnifer-linear"
              class="candidates-page__search-icon text-color-subtext shrink-0"
            />
            <AppTextInput
              v-model="query"
              placeholder="Search Candidates"
              :floating-label="false"
              variant="outlined"
              size="small"
              enterkeyhint="search"
              class="candidates-page__search-input"
            />
            <button
              type="button"
              data-testid="candidates-location"
              class="candidates-page__search-location flex max-w-[45%] shrink-0 items-center gap-1 rounded-full px-3 py-1.5"
              @click="locationModalVisible = true"
            >
              <Icon
                name="solar:map-point-linear"
                size="16"
                class="candidates-page__search-location-icon shrink-0"
              />
              <AppTypography type="caption" class="truncate">
                {{ location.displayLabel }}
              </AppTypography>
            </button>
          </div>

          <div
            class="candidates-page__party order-2 self-start lg:order-2 lg:self-auto"
          >
            <button
              type="button"
              data-testid="candidates-party-trigger"
              class="candidates-page__party-trigger"
              @click="partyModalVisible = true"
            >
              <span class="truncate lg:hidden">{{ partyLabel }}</span>
              <span class="hidden truncate lg:inline">{{
                partyLabelDesktop
              }}</span>
              <Icon
                name="solar:alt-arrow-down-bold"
                class="candidates-page__party-icon shrink-0"
              />
            </button>
          </div>
        </div>

        <EmptyState
          v-if="error && !candidates.length"
          data-testid="candidates-error-state"
          title="Couldn't load candidates"
          illustration="result"
          description="Please try again."
        />
        <div
          v-else-if="isLoading"
          data-testid="candidates-loading"
          class="candidates-page__grid grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-6"
        >
          <AppSkeletonLoader
            v-for="n in 6"
            :key="n"
            :items="[{ height: '150px' }]"
            container-class=""
          />
        </div>
        <div
          v-else-if="candidates.length"
          class="candidates-page__grid grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-6"
        >
          <!-- First 6 cards (~2 mobile viewports) render eagerly so the
               above-the-fold LCP avatar isn't lazy-loaded. -->
          <CandidateCard
            v-for="(candidate, index) in candidates"
            :key="candidate.id"
            :candidate="candidate"
            :eager="index < 6"
            :priority="index === 0"
          />
        </div>
        <EmptyState
          v-else-if="!pending && !settling && isNarrowed"
          data-testid="candidates-empty-state"
          title="No Candidates Found"
          illustration="candidates"
        />
        <EmptyState
          v-else-if="!pending && !settling"
          data-testid="candidates-empty-state"
          title="No Candidates Yet"
          illustration="candidates"
          description="There are no candidates to display at the moment."
        />

        <div v-if="hasMore" class="candidates-page__more flex justify-center">
          <!-- Infinite-scroll sentinel; the button below is the fallback
               when the observer doesn't fire. -->
          <span ref="sentinel" aria-hidden="true"></span>
          <!-- Figma renders this as an underlined brand-green text link, not
               the outlined pill AppButton gave — matching campaigns.index. -->
          <button
            type="button"
            data-testid="show-more-candidates"
            class="candidates-page__more-button"
            @click="loadMore"
          >
            <AppTypography type="subheadline-bold" class="underline"
              >Show More</AppTypography
            >
          </button>
        </div>
      </div>
    </PageHero>
    <LocationPickerModal
      v-if="locationModalVisible"
      v-model:visible="locationModalVisible"
    />
    <CandidatePartyModal
      v-if="partyModalVisible"
      v-model="party"
      v-model:visible="partyModalVisible"
      :options="partyDropdownOptions"
    />
  </div>
</template>

<style scoped lang="scss">
.candidates-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__panel {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;

    // Figma: the whole panel sits in a big white rounded card on desktop;
    // on mobile the tabs/search/filters and cards sit directly on the sand
    // band, each card carrying its own white background.
    @media (min-width: 1024px) {
      padding: $spacing-2xl;
      border-radius: $radius-2xl;
      background-color: $surface-color-bg;
    }
  }

  &__tabs {
    // Figma: complementary/darker-shade-01 (#766035) — no matching design
    // token, so set the literal value.
    border-bottom: 1px solid #766035;
  }

  &__tab {
    @include font-subheadline;
    padding: $spacing-sm;
    border: none;
    border-bottom: 4px solid transparent;
    background: transparent;
    color: #766035;
    font-weight: 600;
    text-align: center;
    cursor: pointer;

    // Desktop only: noticeably wider tab hit area than the mobile tab.
    @media (min-width: 1024px) {
      padding-left: 30px;
      padding-right: 30px;
    }

    &--active {
      border-bottom-color: $text-color-brand;
      color: $text-color-brand;
    }
  }

  &__search {
    width: 100%;
    height: 40px;
    border: 1px solid $border-color;
    border-radius: 9999px;
    padding: 0 $spacing-lg;
    background-color: $surface-color-bg;

    @media (min-width: 1024px) {
      width: 360px;
    }

    @media (max-width: 639.98px) {
      height: 48px;
      border-radius: $radius-md;
    }

    :deep(.app-floating-label-wrapper) {
      display: flex;
      align-items: center;
      width: 100%;
    }

    :deep(.p-inputtext) {
      width: 100%;
      height: 38px;
      border: none;
      background: transparent;
      box-shadow: none;
      padding: 0;

      &:focus,
      &:enabled:focus {
        border: none;
        box-shadow: none;
        outline: none;
      }

      @media (max-width: 639.98px) {
        &::placeholder {
          color: $text-color-tag;
        }
      }
    }
  }

  &__search-icon {
    font-size: 1.125rem;
  }

  &__search-location {
    background-color: $surface-color-tag;
    border: 1px solid $surface-color-disabled;
    color: $text-color-primary;

    @media (min-width: 1024px) {
      display: none;
    }
  }

  &__search-location-icon {
    color: $text-color-tag;
  }

  // Figma: mobile keeps the dark-gold pill trigger, but desktop uses the
  // light surfaces/fields-tags chip (#f6f4f0 bg, #e8e3db border, #201e1a
  // text) — same distinction as the Parties page's filter chip (VTW-454).
  &__party-trigger {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    max-width: 100%;
    height: 40px;
    padding: 0 $spacing-lg;
    border: none;
    border-radius: 9999px;
    background-color: #766035;
    color: $text-color-white;
    font: inherit;
    font-weight: 600;
    cursor: pointer;

    @media (min-width: 1024px) {
      border: 1px solid #e8e3db;
      background-color: #f6f4f0;
      color: #201e1a;
      font-weight: 400;
    }
  }

  &__more {
    margin-top: $spacing-md;
  }

  &__more-button {
    padding: $spacing-sm 0;
    border: none;
    background: transparent;
    color: $text-color-brand;
    cursor: pointer;
  }
}
</style>
